import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, MailWarning } from "lucide-react";
import { fmtDateTime } from "../../lib/format";
import { fmtValidUntil } from "../../lib/platformValidation";
import PlatformLifecycleBadge from "./PlatformLifecycleBadge";

const Seats = ({ usage, limit, testId }) => {
  const over = Number(usage) > Number(limit);
  return (
    <span
      className={over ? "qw-platform-over" : undefined}
      // Phase 2D.2 checkpoint 2B — the first number is AUTHORITATIVE usage:
      // active members plus pending, non-expired invitations. Saying only
      // "активни" here would under-report a reserved seat.
      title="Заети места (активни потребители плюс чакащи покани) / лицензирани места"
      data-testid={testId}
    >
      {usage} / {limit}
    </span>
  );
};

/**
 * Organization overview: lifecycle state, who owns it (or who is invited),
 * license and seats. Read-only — every change happens on the detail screen.
 */
export const PlatformOrgTable = ({ items }) => {
  const { t } = useTranslation();

  return (
    <div className="qw-table-wrap" tabIndex={0} role="region" aria-label={t("platform.organizations")}>
      <table className="qw-table" data-testid="platform-org-table">
        <thead>
          <tr>
            <th>{t("platform.col_organization")}</th>
            <th>{t("platform.col_lifecycle")}</th>
            <th>{t("platform.col_owner")}</th>
            <th>{t("platform.col_status")}</th>
            <th>{t("platform.col_valid_until")}</th>
            <th>{t("platform.owner_seats")}</th>
            <th>{t("platform.manager_seats")}</th>
            <th>{t("platform.sales_seats")}</th>
            <th>{t("platform.col_updated")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id} data-testid={`platform-org-row-${row.id}`}>
              <td data-testid={`platform-org-name-${row.id}`}>
                {row.name}
                {row.over_limit && (
                  <span
                    className="qw-platform-warn"
                    title={t("platform.over_limit_hint")}
                    data-testid={`platform-org-over-limit-${row.id}`}
                  >
                    <AlertTriangle size={13} strokeWidth={2} aria-hidden="true" />
                    {t("platform.over_limit")}
                  </span>
                )}
              </td>
              <td>
                <PlatformLifecycleBadge
                  status={row.lifecycle_status}
                  testId={`platform-org-lifecycle-${row.id}`}
                />
              </td>
              <td data-testid={`platform-org-owner-${row.id}`}>
                {row.owner_email ? (
                  row.owner_email
                ) : row.pending_owner_email ? (
                  <span data-testid={`platform-org-pending-owner-${row.id}`}>
                    {t("platform.owner_invited_to", { email: row.pending_owner_email })}
                    {row.invitation_delivery_status === "delivery_failed" && (
                      <span
                        className="qw-platform-warn"
                        title={t("platform.delivery_failed")}
                        data-testid={`platform-org-delivery-failed-${row.id}`}
                      >
                        <MailWarning size={13} strokeWidth={2} aria-hidden="true" />
                        {t("platform.delivery_failed")}
                      </span>
                    )}
                  </span>
                ) : (
                  t("platform.no_owner_yet")
                )}
              </td>
              <td data-testid={`platform-org-status-${row.id}`}>
                {row.status ? t(`platform.status_${row.status}`) : "—"}
              </td>
              <td data-testid={`platform-org-valid-until-${row.id}`}>
                {fmtValidUntil(row.valid_until, t("platform.indefinite"))}
              </td>
              <td>
                <Seats
                  usage={row.owner_usage}
                  limit={row.owner_limit}
                  testId={`platform-org-owner-seats-${row.id}`}
                />
              </td>
              <td>
                <Seats
                  usage={row.manager_usage}
                  limit={row.manager_limit}
                  testId={`platform-org-manager-seats-${row.id}`}
                />
              </td>
              <td>
                <Seats
                  usage={row.sales_usage}
                  limit={row.sales_limit}
                  testId={`platform-org-sales-seats-${row.id}`}
                />
              </td>
              <td>{fmtDateTime(row.updated_at)}</td>
              <td>
                <Link
                  className="qw-btn ghost sm"
                  to={`/platform-admin/organizations/${row.id}`}
                  data-testid={`platform-org-open-${row.id}`}
                >
                  {t("platform.open")}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PlatformOrgTable;
